import { cn, cva, platformProps, type VariantProps } from "../../lib";
import { webOnly } from "../../lib/web-role";
import { Box, Separator } from "../../primitives";
import type {
  DividerEmphasis,
  DividerOrientation,
  DividerProps,
  DividerSize,
  DividerTone,
  DividerVariant,
} from "./types";

const dividerVariants = cva("shrink-0", {
  variants: {
    orientation: {
      horizontal: "w-full",
      vertical: "self-stretch",
    },
    variant: {
      solid: "",
      dashed: "relative overflow-hidden",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
    variant: "solid",
  },
});

// Figma's `_outline-width/outline-1·2·3`
const sizeClasses: Record<DividerOrientation, Record<DividerSize, string>> = {
  horizontal: { "1pt": "h-outline-1", "2pt": "h-outline-2", "3pt": "h-outline-3" },
  vertical: { "1pt": "w-outline-1", "2pt": "w-outline-2", "3pt": "w-outline-3" },
};

// iOS draws a one-sided dashed border solid; it dashes only a border that is
// the same on all four sides. So the dashed line is a fully bordered box, twice
// the line's thickness, clipped to its top (or left) edge.
const dashedLineClasses: Record<DividerOrientation, Record<DividerSize, string>> = {
  horizontal: {
    "1pt": "inset-x-0 top-0 h-0.5 border-1",
    "2pt": "inset-x-0 top-0 h-1 border-2",
    "3pt": "inset-x-0 top-0 h-1.5 border-3",
  },
  vertical: {
    "1pt": "inset-y-0 left-0 w-0.5 border-1",
    "2pt": "inset-y-0 left-0 w-1 border-2",
    "3pt": "inset-y-0 left-0 w-1.5 border-3",
  },
};

// Solid: color via background, Dashed: color via border
const emphasisClasses: Record<DividerVariant, Record<DividerEmphasis, string>> = {
  solid: {
    low: "bg-luce-neutral-border-lowEmphasis",
    medium: "bg-luce-neutral-border-mediumEmphasis",
  },
  dashed: {
    low: "border-luce-neutral-border-lowEmphasis",
    medium: "border-luce-neutral-border-mediumEmphasis",
  },
};

// The brand tones, as colour alpha rather than element opacity
const toneClasses: Record<DividerVariant, Record<Exclude<DividerTone, "neutral">, string>> = {
  solid: {
    brand: "bg-luce-brand-primaryBorder/50",
    "on-primary": "bg-luce-brand-onPrimary/25",
  },
  dashed: {
    brand: "border-luce-brand-primaryBorder/50",
    "on-primary": "border-luce-brand-onPrimary/25",
  },
};

const lineColorClass = (variant: DividerVariant, tone: DividerTone, emphasis: DividerEmphasis) =>
  tone === "neutral" ? emphasisClasses[variant][emphasis] : toneClasses[variant][tone];

export type DividerVariantProps = VariantProps<typeof dividerVariants>;

export const Divider = ({
  orientation = "horizontal",
  size = "1pt",
  variant = "solid",
  emphasis = "medium",
  tone = "neutral",
  className,
  testID,
}: DividerProps) =>
  variant === "dashed" ? (
    // The root carries what a solid line's root does: the decorative role and,
    // on web, `data-orientation` (the inner line has them too)
    <Box
      {...platformProps({ testID, slot: "divider" })}
      accessibilityRole="none"
      data-orientation={webOnly(orientation)}
      className={cn(
        dividerVariants({ orientation, variant }),
        sizeClasses[orientation][size],
        className,
      )}
    >
      <Separator
        orientation={orientation}
        className={cn(
          "absolute border-dashed",
          dashedLineClasses[orientation][size],
          lineColorClass("dashed", tone, emphasis),
        )}
      />
    </Box>
  ) : (
    <Separator
      {...platformProps({ testID, slot: "divider" })}
      orientation={orientation}
      className={cn(
        dividerVariants({ orientation, variant }),
        sizeClasses[orientation][size],
        lineColorClass("solid", tone, emphasis),
        className,
      )}
    />
  );

Divider.displayName = "Divider";
